/**
 * Manufacturing management → Edit batch (parity with Manufacturing Dashboard **Edit Batch**).
 *
 * Entry by ship-to address: `?batch_id=` (batch row address) or `?sales_order_id=` (order ship-to), plus `location=`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, StickyNote } from "lucide-react";

import { BatchTeamNotesGlyph } from "@/components/manufacturing/BatchTeamNotesGlyph";
import {
  type BatchTeamNoteRow,
  type BatchTeamNotesPreview,
} from "@/components/manufacturing/BatchTeamNotesPanel";

import { HubPageTitle } from "@/components/HubPageTitle";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import {
  buildManufacturingEditBatchPath,
  buildManufacturingEditBatchPathForBatch,
} from "@/lib/manufacturingEditBatchPath";
import { MRP_LOCATION_PUBLIC_LOCATION_ID, type MrpLocation } from "@/lib/mrp";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";
import {
  CreateAlternateOrderModal,
  type CreateAlternateOrderModalPrefill,
} from "@/pages/manufacturing/CreateAlternateOrderModal";

const DEFAULT_EDIT_BATCH_LOCATIONS = ["sgs", "suntronic"] as const satisfies readonly MrpLocation[];

function batchLocationOptionEntries(): { key: MrpLocation; id: number }[] {
  return DEFAULT_EDIT_BATCH_LOCATIONS.map((key) => ({ key, id: MRP_LOCATION_PUBLIC_LOCATION_ID[key] }));
}

function toHtmlDateValue(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === "") return "";
  const slice = String(raw).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/u.test(slice) ? slice : "";
}

function locationIdsFromSearchParam(raw: string | null): string {
  if (raw == null || raw.trim() === "") {
    return DEFAULT_EDIT_BATCH_LOCATIONS.map((key) => String(MRP_LOCATION_PUBLIC_LOCATION_ID[key])).join(",");
  }
  return raw.trim();
}

function formatLocationLabel(locationId: number): string {
  const entry = (Object.entries(MRP_LOCATION_PUBLIC_LOCATION_ID) as [MrpLocation, number][]).find(
    ([, id]) => id === locationId,
  );
  return entry != null ? entry[0].toUpperCase() : `Location ${String(locationId)}`;
}

interface EditBatchAddress {
  recipient: string;
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface EditBatchContextLine {
  sales_order_number: string | null;
  sgs_ship_by_date: string | null;
  sales_order_item_id: number;
  sku: string;
  quantity: number;
  sales_order_id: number;
  megaventory_id: number | null;
  remaining_quantity: number;
  remark: string | null;
  build_to_order: boolean;
}

interface EditBatchContextBatch {
  batch_id: number;
  mfr_ship_by_date: string | null;
  location_id: number;
}

interface EditBatchContextResponse {
  sales_order: {
    id: number;
    number: string | null;
    location_id: number;
    sgs_ship_by_date: string | null;
    status: string | null;
  } | null;
  address: EditBatchAddress;
  lines: EditBatchContextLine[];
  batches: EditBatchContextBatch[];
  same_address_sales_orders: SameAddressSalesOrderRow[];
}

interface SameAddressSalesOrderRow {
  id: number;
  number: string | null;
  location_id: number;
  sgs_ship_by_date: string | null;
  status: string | null;
  fully_allocated: boolean;
}

interface BatchItemRow {
  batch_item_id: number;
  build_order_id: number | null;
  build_order: string | null;
  internal_build_order: string | null;
  build_order_rebuild: boolean | null;
  sku: string;
  quantity: string | number;
  sales_order: string | null;
  sgs_ship_by_date: string | null;
  remark: string | null;
  build_to_order: boolean;
  sales_order_item_id: number | null;
  tla_build_order_id: number | null;
  tla_build_order: string | null;
  tla_purchase_order: string | null;
}

function formatShipByShort(isoDay: string | null | undefined): string {
  if (isoDay == null || String(isoDay).trim() === "") {
    return "—";
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDay).trim());
  if (match == null) {
    return String(isoDay).trim();
  }
  const [, yyyy, mm, dd] = match;
  return `${mm}-${dd}-${yyyy}`;
}

function sameAddressSalesOrderOptionLabel(row: SameAddressSalesOrderRow): string {
  const numberPart =
    row.number != null && row.number.trim() !== "" ? row.number.trim() : `id ${String(row.id)}`;
  const site = formatLocationLabel(row.location_id);
  const ship = formatShipByShort(row.sgs_ship_by_date);
  const base = `${numberPart} · ${site} · ship by ${ship}`;
  return row.fully_allocated ? `${base} (fully allocated)` : base;
}

function formatAddressBlock(address: EditBatchAddress): string {
  const line2 = address.line2.trim() !== "" ? `${address.line2}, ` : "";
  const line3 = address.line3.trim() !== "" ? `${address.line3}, ` : "";
  const cityLine = [address.city, address.state, address.zip].filter((piece) => piece.trim() !== "").join(" ");
  return [
    address.recipient.trim() !== "" ? address.recipient : null,
    address.line1.trim() !== "" ? address.line1 : null,
    `${line2}${line3}${cityLine}`.trim() !== "" ? `${line2}${line3}${cityLine}`.replace(/, $/u, "") : null,
    address.country.trim() !== "" ? address.country : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

interface LineDraft {
  quantityText: string;
}

function teamNoteAuthorLabel(note: BatchTeamNoteRow): string {
  const displayName = note.created_by_display_name?.trim();
  if (displayName != null && displayName !== "") {
    return displayName;
  }
  return note.created_by_email;
}

function SalesOrderLineRemarkGlyph({ remark }: { remark: string | null | undefined }): JSX.Element | null {
  const remarkText = remark != null ? remark.trim() : "";
  if (remarkText === "") {
    return null;
  }
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-amber-200 bg-amber-50 text-amber-700 shadow-sm hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          aria-label="Sales order line remark"
        >
          <StickyNote className="h-3.5 w-3.5" aria-hidden />
        </button>
      }
    >
      <p className="max-w-xs whitespace-pre-wrap text-sm text-slate-800">{remarkText}</p>
    </HoverHintTrigger>
  );
}

export function EditBatchPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const [searchParams] = useSearchParams();
  const salesOrderIdRaw = searchParams.get("sales_order_id");
  const salesOrderId = useMemo(() => {
    const n = Number.parseInt(String(salesOrderIdRaw ?? "").trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [salesOrderIdRaw]);

  const locationParam = useMemo(
    () => locationIdsFromSearchParam(searchParams.get("location")),
    [searchParams],
  );

  const entryBatchId = useMemo(() => {
    const raw = searchParams.get("batch_id") ?? searchParams.get("highlight_batch_id");
    const n = Number.parseInt(String(raw ?? "").trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const hasEntry = salesOrderId != null || entryBatchId != null;

  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [newBatchDialogOpen, setNewBatchDialogOpen] = useState(false);
  const [newBatchLocationId, setNewBatchLocationId] = useState<number>(MRP_LOCATION_PUBLIC_LOCATION_ID.sgs);
  const [lineDrafts, setLineDrafts] = useState<Record<number, LineDraft>>({});
  const [pendingAddLineItemIds, setPendingAddLineItemIds] = useState(() => new Set<number>());
  const [pendingRemoveBatchItemIds, setPendingRemoveBatchItemIds] = useState(() => new Set<number>());
  const [batchMfrDateDraft, setBatchMfrDateDraft] = useState("");
  const [alternateOrderModalOpen, setAlternateOrderModalOpen] = useState(false);
  const [alternateOrderPrefill, setAlternateOrderPrefill] = useState<CreateAlternateOrderModalPrefill | undefined>(
    undefined,
  );

  useEffect(() => {
    setSelectedBatchId(null);
    setLineDrafts({});
    setPendingAddLineItemIds(new Set());
    setPendingRemoveBatchItemIds(new Set());
    setBatchMfrDateDraft("");
  }, [salesOrderId, entryBatchId]);

  const contextQuery = useQuery<EditBatchContextResponse, ApiError>({
    queryKey: ["manufacturing", "edit-batch", "context", salesOrderId, entryBatchId, locationParam],
    queryFn: () => {
      if (salesOrderId != null) {
        return apiFetch<EditBatchContextResponse>(
          `/api/manufacturing/edit-batch/sales-order/${String(salesOrderId)}/context`,
          {
            query: {
              location: locationParam,
              ...(entryBatchId != null ? { highlight_batch_id: entryBatchId } : {}),
            },
          },
        );
      }
      return apiFetch<EditBatchContextResponse>(
        `/api/manufacturing/edit-batch/batch/${String(entryBatchId)}/context`,
        { query: { location: locationParam } },
      );
    },
    enabled: hasEntry,
  });

  const contextLoaded =
    !contextQuery.isError &&
    contextQuery.data != null &&
    (salesOrderId == null
      ? entryBatchId != null
      : contextQuery.data.sales_order != null && contextQuery.data.sales_order.id === salesOrderId);

  const focusSalesOrderId =
    salesOrderId ?? (contextLoaded ? (contextQuery.data?.sales_order?.id ?? null) : null);

  const lines = contextLoaded ? contextQuery.data.lines : [];
  const batches = contextLoaded ? contextQuery.data.batches : [];

  const selectedBatch = useMemo(
    () => (selectedBatchId == null ? undefined : batches.find((row) => row.batch_id === selectedBatchId)),
    [batches, selectedBatchId],
  );

  useEffect(() => {
    if (selectedBatch == null) {
      setBatchMfrDateDraft("");
      return;
    }
    setBatchMfrDateDraft(toHtmlDateValue(selectedBatch.mfr_ship_by_date));
  }, [selectedBatch?.batch_id, selectedBatch?.mfr_ship_by_date]);

  useEffect(() => {
    if (lines.length === 0) return;
    setLineDrafts((previous) => {
      const next = { ...previous };
      for (const line of lines) {
        if (next[line.sales_order_item_id] == null) {
          next[line.sales_order_item_id] = {
            quantityText: String(line.remaining_quantity),
          };
        }
      }
      return next;
    });
  }, [lines]);

  useEffect(() => {
    if (batches.length === 0) {
      setSelectedBatchId(null);
      return;
    }
    const batchIds = new Set(batches.map((row) => row.batch_id));
    if (selectedBatchId != null && batchIds.has(selectedBatchId)) {
      return;
    }
    if (entryBatchId != null && batchIds.has(entryBatchId)) {
      setSelectedBatchId(entryBatchId);
      return;
    }
    setSelectedBatchId(batches[0]?.batch_id ?? null);
  }, [batches, entryBatchId, selectedBatchId]);

  const batchItemsQuery = useQuery<{ rows: BatchItemRow[] }, ApiError>({
    queryKey: ["manufacturing", "edit-batch", "batch-items", selectedBatchId],
    queryFn: () =>
      apiFetch<{ rows: BatchItemRow[] }>("/api/manufacturing/edit-batch/batch-items", {
        query: { batch_id: selectedBatchId ?? 0 },
      }),
    enabled: selectedBatchId != null,
  });

  const batchTeamNotesQuery = useQuery<{ notes: BatchTeamNoteRow[] }, ApiError>({
    queryKey: ["manufacturing", "batch-team-notes", selectedBatchId],
    queryFn: () =>
      apiFetch<{ notes: BatchTeamNoteRow[] }>(
        `/api/manufacturing/batches/${String(selectedBatchId)}/team-notes`,
      ),
    enabled: selectedBatchId != null,
  });

  const batchTeamNoteCount = batchTeamNotesQuery.data?.notes.length ?? 0;
  const latestBatchTeamNote = useMemo((): BatchTeamNotesPreview | null => {
    const noteRows = batchTeamNotesQuery.data?.notes ?? [];
    const latest =
      noteRows.find((note) => note.deleted_at == null || note.deleted_at.trim() === "") ?? noteRows[0];
    if (latest == null) return null;
    return {
      text: latest.note_text,
      createdAt: latest.created_at,
      authorLabel: teamNoteAuthorLabel(latest),
      deleted: latest.deleted_at != null && latest.deleted_at.trim() !== "",
    };
  }, [batchTeamNotesQuery.data?.notes]);

  const invalidateEditBatch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "edit-batch"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "incoming-sales-orders"] });
  }, [queryClient]);

  const createBatchMutation = useMutation<{ batch_id: number }, ApiError, void>({
    mutationFn: async () => {
      if (contextQuery.data == null) {
        throw new Error("Missing edit batch context.");
      }
      const body: {
        location_id: number;
        sales_order_id?: number;
        source_batch_id?: number;
      } = { location_id: newBatchLocationId };
      if (focusSalesOrderId != null) {
        body.sales_order_id = focusSalesOrderId;
      } else {
        const sourceBatchId = selectedBatchId ?? entryBatchId;
        if (sourceBatchId == null) {
          throw new Error("Select a batch or sales order before creating a new batch.");
        }
        body.source_batch_id = sourceBatchId;
      }
      return apiFetch<{ batch_id: number }>("/api/manufacturing/edit-batch/batch", {
        method: "POST",
        body,
      });
    },
    onSuccess: async (data) => {
      await invalidateEditBatch();
      setSelectedBatchId(data.batch_id);
      setNewBatchDialogOpen(false);
    },
  });

  const updateBatchMutation = useMutation<
    { ok: boolean },
    ApiError,
    { batchId: number; location_id?: number; mfr_ship_by_date?: string | null }
  >({
    mutationFn: async (body) =>
      apiFetch<{ ok: boolean }>(`/api/manufacturing/edit-batch/batch/${String(body.batchId)}`, {
        method: "PATCH",
        body: {
          ...(body.location_id != null ? { location_id: body.location_id } : {}),
          ...(Object.prototype.hasOwnProperty.call(body, "mfr_ship_by_date")
            ? { mfr_ship_by_date: body.mfr_ship_by_date }
            : {}),
        },
      }),
    onSuccess: async () => {
      await invalidateEditBatch();
    },
  });

  const addLineMutation = useMutation<
    { batch_item_id: number | null; build_order_id: number | null },
    ApiError,
    {
      sales_order_item_id: number;
      sku: string;
      quantity: number;
      work_order: string | number;
      rebuild_component_sku?: string;
    }
  >({
    mutationFn: async (body) => {
      if (selectedBatchId == null) throw new Error("Select a batch first.");
      return apiFetch<{ batch_item_id: number | null; build_order_id: number | null }>(
        "/api/manufacturing/edit-batch/batch-item",
        {
          method: "POST",
          body: {
            batch_id: selectedBatchId,
            sales_order_item_id: body.sales_order_item_id,
            sku: body.sku,
            quantity: body.quantity,
            work_order: body.work_order,
            ...(body.work_order === "rebuild" && body.rebuild_component_sku != null
              ? { rebuild_component_sku: body.rebuild_component_sku }
              : {}),
          },
        },
      );
    },
    onSuccess: async () => {
      await invalidateEditBatch();
    },
  });

  const removeBatchLineMutation = useMutation<{ ok: boolean }, ApiError, number>({
    mutationFn: async (batchItemId) =>
      apiFetch<{ ok: boolean }>(`/api/manufacturing/edit-batch/batch-item/${String(batchItemId)}`, {
        method: "DELETE",
      }),
    onSuccess: async () => {
      await invalidateEditBatch();
    },
  });

  const deleteBatchMutation = useMutation<{ ok: boolean; batch_id: number }, ApiError, number>({
    mutationFn: async (batchId) =>
      apiFetch<{ ok: boolean; batch_id: number }>(`/api/manufacturing/edit-batch/batch/${String(batchId)}`, {
        method: "DELETE",
      }),
    onSuccess: async (_data, deletedBatchId) => {
      const prior = queryClient.getQueryData<EditBatchContextResponse>([
        "manufacturing",
        "edit-batch",
        "context",
        salesOrderId,
        entryBatchId,
        locationParam,
      ]);
      const remainingBatches = (prior?.batches ?? []).filter((row) => row.batch_id !== deletedBatchId);
      const anchorSalesOrderId =
        salesOrderId ??
        prior?.sales_order?.id ??
        prior?.same_address_sales_orders?.find((row) => !row.fully_allocated)?.id ??
        prior?.same_address_sales_orders?.[0]?.id ??
        null;

      if (selectedBatchId === deletedBatchId) {
        setSelectedBatchId(null);
      }

      if (anchorSalesOrderId != null) {
        navigate(
          buildManufacturingEditBatchPath({
            salesOrderId: anchorSalesOrderId,
            batchId: remainingBatches[0]?.batch_id ?? null,
          }),
          { replace: true },
        );
      } else if (remainingBatches.length > 0) {
        navigate(buildManufacturingEditBatchPathForBatch(remainingBatches[0].batch_id), { replace: true });
      } else {
        navigate("/manufacturing-management/production-calendar", { replace: true });
      }

      await queryClient.removeQueries({ queryKey: ["manufacturing", "edit-batch", "context"] });
      await invalidateEditBatch();
    },
  });

  const beginAddLine = useCallback(
    (
      salesOrderItemId: number,
      variables: {
        sales_order_item_id: number;
        sku: string;
        quantity: number;
        work_order: string | number;
        rebuild_component_sku?: string;
      },
    ) => {
      setPendingAddLineItemIds((previous) => {
        const next = new Set(previous);
        next.add(salesOrderItemId);
        return next;
      });
      addLineMutation.mutate(variables, {
        onSettled: () => {
          setPendingAddLineItemIds((previous) => {
            const next = new Set(previous);
            next.delete(salesOrderItemId);
            return next;
          });
        },
      });
    },
    [addLineMutation],
  );

  const beginRemoveBatchLine = useCallback(
    (batchItemId: number) => {
      setPendingRemoveBatchItemIds((previous) => {
        const next = new Set(previous);
        next.add(batchItemId);
        return next;
      });
      removeBatchLineMutation.mutate(batchItemId, {
        onSettled: () => {
          setPendingRemoveBatchItemIds((previous) => {
            const next = new Set(previous);
            next.delete(batchItemId);
            return next;
          });
        },
      });
    },
    [removeBatchLineMutation],
  );

  const tabBreadcrumbLabel =
    selectedBatchId != null
      ? `Batch ${String(selectedBatchId)}`
      : entryBatchId != null
        ? `Batch ${String(entryBatchId)}`
        : "Edit batch";

  if (!hasEntry) {
    return (
      <div className="space-y-4">
        <HubPageTitle titleClassName="text-lg" breadcrumbLabel={tabBreadcrumbLabel}>
          Edit batch
        </HubPageTitle>
        <Card>
          <CardBody className="text-sm text-slate-600">
            Open this page with a <span className="font-mono text-xs">batch_id</span> or{" "}
            <span className="font-mono text-xs">sales_order_id</span> query parameter (same ship-to address and end
            user groups batches and orders on this screen).
          </CardBody>
        </Card>
      </div>
    );
  }

  const so = contextLoaded ? (contextQuery.data.sales_order ?? undefined) : undefined;
  const address = contextLoaded ? contextQuery.data.address : undefined;

  const sameAddressOptions: SameAddressSalesOrderRow[] = useMemo(() => {
    if (!contextLoaded) return [];
    return contextQuery.data.same_address_sales_orders ?? [];
  }, [contextLoaded, contextQuery.data?.same_address_sales_orders]);

  return (
    <div className="space-y-4">
      <HubPageTitle titleClassName="text-lg" breadcrumbLabel={tabBreadcrumbLabel}>
        Edit batch
      </HubPageTitle>

      {contextLoaded && address != null ? (
        <div className="px-2 text-center">
          <HoverHintContent hoverHint={formatAddressBlock(address)}>
            <p className="text-sm font-semibold text-slate-900">{formatAddressBlock(address)}</p>
          </HoverHintContent>
        </div>
      ) : null}

      {contextQuery.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-rose-800">Could not load edit batch context</p>
            <p className="mt-1 text-sm text-rose-700">{contextQuery.error.message}</p>
          </CardBody>
        </Card>
      ) : null}

      {!contextQuery.isError && !contextLoaded && (contextQuery.isLoading || contextQuery.isFetching) ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-600">Loading…</CardBody>
        </Card>
      ) : null}

      {contextLoaded ? (
        <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
          <Card className="min-w-0">
            <CardHeader className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base">Sales order</CardTitle>
              </div>
              {sameAddressOptions.length > 0 ? (
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-700">Open sales order</span>
                  <select
                    className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm font-semibold text-slate-900"
                    value={so?.id ?? ""}
                    onChange={(event) => {
                      const id = Number.parseInt(event.target.value, 10);
                      if (!Number.isFinite(id) || id === so?.id) return;
                      const next = new URLSearchParams();
                      next.set("sales_order_id", String(id));
                      next.set("location", locationParam);
                      const batchParam = searchParams.get("batch_id") ?? searchParams.get("highlight_batch_id");
                      if (batchParam != null && batchParam.trim() !== "") {
                        next.set("batch_id", batchParam.trim());
                      }
                      navigate(`/manufacturing-management/edit-batch?${next.toString()}`);
                    }}
                  >
                    {so == null ? (
                      <option value="">Select a sales order…</option>
                    ) : null}
                    {sameAddressOptions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {sameAddressSalesOrderOptionLabel(row)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <p className="text-sm text-slate-600">
                  No open sales orders share this ship-to address and end user. You can still manage batches on the
                  right.
                </p>
              )}
            </CardHeader>
            <CardBody className="space-y-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-600">Lines to schedule</p>
              {so == null ? (
                <p className="text-sm text-slate-600">
                  Select a sales order above to schedule lines onto a batch.
                </p>
              ) : lines.length === 0 ? (
                <p className="text-sm text-slate-600">No open lines remain to schedule for this sales order.</p>
              ) : (
                <div className="-mx-1 overflow-x-auto">
                  <table className="w-full min-w-[520px] text-left text-xs text-slate-800">
                    <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-2 py-1.5">SKU</th>
                        <th className="px-2 py-1.5 text-right tabular-nums">On order</th>
                        <th className="px-2 py-1.5 text-right tabular-nums">Remaining</th>
                        <th className="px-2 py-1.5">Quantity</th>
                        <th className="w-10 px-1 py-1.5"> </th>
                        <th className="w-16 px-1 py-1.5"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => {
                        const draft = lineDrafts[line.sales_order_item_id] ?? {
                          quantityText: String(line.remaining_quantity),
                        };
                        const rowPending = pendingAddLineItemIds.has(line.sales_order_item_id);
                        return (
                          <tr key={line.sales_order_item_id} className="border-b border-slate-50 last:border-none">
                            <td className="whitespace-normal px-2 py-1.5 align-middle text-sm">
                              <span className="inline-flex items-start gap-1">
                                <span className="break-all font-mono">{line.sku}</span>
                                <SalesOrderLineRemarkGlyph remark={line.remark} />
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-right align-middle tabular-nums text-sm">{line.quantity}</td>
                            <td className="px-2 py-1.5 text-right align-middle tabular-nums text-sm">
                              {line.remaining_quantity}
                            </td>
                            <td className="px-2 py-1.5 align-middle">
                              <Input
                                className="h-8 w-20 text-right text-sm tabular-nums"
                                value={draft.quantityText}
                                disabled={rowPending}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setLineDrafts((previous) => ({
                                    ...previous,
                                    [line.sales_order_item_id]: { quantityText: value },
                                  }));
                                }}
                                inputMode="numeric"
                              />
                            </td>
                            <td className="px-1 py-1.5 align-middle">
                              <ReadOnlyHoverLayer
                                active={!hubWritesAllowed || selectedBatchId == null}
                                hoverHint={
                                  selectedBatchId == null
                                    ? "Select or create a batch before adding lines."
                                    : MANUFACTURING_HUB_READ_ONLY
                                }
                                layout="inline"
                              >
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  className="inline-flex h-8 min-w-[2.25rem] items-center justify-center px-1.5 font-mono text-sm tabular-nums"
                                  disabled={!hubWritesAllowed || selectedBatchId == null || rowPending}
                                  aria-label="Add to batch"
                                  onClick={() => {
                                    const qty = Number.parseInt(draft.quantityText.trim(), 10);
                                    if (!Number.isFinite(qty) || qty <= 0) {
                                      return;
                                    }
                                    if (qty > line.remaining_quantity) {
                                      return;
                                    }
                                    beginAddLine(line.sales_order_item_id, {
                                      sales_order_item_id: line.sales_order_item_id,
                                      sku: line.sku,
                                      quantity: qty,
                                      work_order: line.build_to_order ? "new" : "stock",
                                    });
                                  }}
                                >
                                  {rowPending ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                  ) : (
                                    ">>"
                                  )}
                                </Button>
                              </ReadOnlyHoverLayer>
                            </td>
                            <td className="px-1 py-1.5 align-middle">
                              <ReadOnlyHoverLayer
                                active={!hubWritesAllowed}
                                hoverHint={MANUFACTURING_HUB_READ_ONLY}
                                layout="inline"
                              >
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 whitespace-nowrap px-1.5 text-xs"
                                  disabled={!hubWritesAllowed}
                                  onClick={() => {
                                    setAlternateOrderPrefill({
                                      sku: line.sku,
                                      batchId: selectedBatchId,
                                      salesOrderId: focusSalesOrderId,
                                      targetDate:
                                        batchMfrDateDraft.trim() !== ""
                                          ? batchMfrDateDraft.trim()
                                          : toHtmlDateValue(selectedBatch?.mfr_ship_by_date) || null,
                                    });
                                    setAlternateOrderModalOpen(true);
                                  }}
                                >
                                  Alt order
                                </Button>
                              </ReadOnlyHoverLayer>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {addLineMutation.isError ? (
                <p className="text-sm text-rose-700">{addLineMutation.error.message}</p>
              ) : null}
            </CardBody>
          </Card>

          <Card className="min-w-0">
            <CardHeader className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Batch</CardTitle>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    disabled={!hubWritesAllowed || createBatchMutation.isPending || !contextLoaded}
                    onClick={() => setNewBatchDialogOpen(true)}
                  >
                    New batch
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
              {batches.length === 0 ? (
                <p className="text-sm text-slate-700">
                  No existing batch matches this ship-to address and end user for the selected sites. Create a new
                  batch to start scheduling lines.
                </p>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex min-w-[10rem] flex-1 flex-col gap-1">
                    <span className="text-xs font-medium text-slate-700">Open batch</span>
                    <select
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                      value={selectedBatchId ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSelectedBatchId(value === "" ? null : Number.parseInt(value, 10));
                      }}
                    >
                      {batches.map((batch) => (
                        <option key={batch.batch_id} value={batch.batch_id}>
                          Batch {String(batch.batch_id)}
                          {batch.mfr_ship_by_date != null && batch.mfr_ship_by_date.trim() !== ""
                            ? ` · MFR ship ${batch.mfr_ship_by_date.trim()}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selectedBatchId != null ? (
                    <>
                      <label className="flex min-w-[6rem] flex-col gap-1 text-xs text-slate-600">
                        <span className="font-medium text-slate-700">Batch location</span>
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint={MANUFACTURING_HUB_READ_ONLY}
                          layout="block"
                        >
                          <select
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                            disabled={!hubWritesAllowed || updateBatchMutation.isPending}
                            value={selectedBatch?.location_id ?? ""}
                            onChange={(event) => {
                              const value = Number.parseInt(event.target.value, 10);
                              if (!Number.isFinite(value)) return;
                              updateBatchMutation.mutate({ batchId: selectedBatchId, location_id: value });
                            }}
                          >
                            {batchLocationOptionEntries().map(({ key, id }) => (
                              <option key={key} value={id}>
                                {formatLocationLabel(id)}
                              </option>
                            ))}
                          </select>
                        </ReadOnlyHoverLayer>
                      </label>
                      <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-slate-600">
                        <span className="font-medium text-slate-700">Manufacturer ship-by</span>
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint={MANUFACTURING_HUB_READ_ONLY}
                          layout="block"
                        >
                          <input
                            type="date"
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                            disabled={!hubWritesAllowed || updateBatchMutation.isPending}
                            value={batchMfrDateDraft}
                            onChange={(event) => setBatchMfrDateDraft(event.target.value)}
                            onBlur={() => {
                              if (selectedBatch == null) return;
                              const server = toHtmlDateValue(selectedBatch.mfr_ship_by_date);
                              if (batchMfrDateDraft === server) return;
                              updateBatchMutation.mutate({
                                batchId: selectedBatchId,
                                mfr_ship_by_date: batchMfrDateDraft.trim() === "" ? null : batchMfrDateDraft.trim(),
                              });
                            }}
                          />
                        </ReadOnlyHoverLayer>
                      </label>
                    </>
                  ) : null}
                </div>
              )}
            </CardHeader>
            <CardBody className="space-y-3">
              {updateBatchMutation.isError ? (
                <p className="text-sm text-rose-700">{updateBatchMutation.error.message}</p>
              ) : null}
              {removeBatchLineMutation.isError ? (
                <p className="text-sm text-rose-700">{removeBatchLineMutation.error.message}</p>
              ) : null}
              {deleteBatchMutation.isError ? (
                <p className="text-sm text-rose-700">{deleteBatchMutation.error.message}</p>
              ) : null}
              {selectedBatchId != null ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-600">Batch lines</p>
                    <BatchTeamNotesGlyph
                      batchId={selectedBatchId}
                      teamNoteCount={batchTeamNoteCount}
                      latestTeamNote={latestBatchTeamNote}
                      hubWritesAllowed={hubWritesAllowed}
                    />
                  </div>
                  {batchItemsQuery.isError ? (
                    <p className="text-sm text-rose-700">{batchItemsQuery.error.message}</p>
                  ) : null}
                  {batchItemsQuery.isLoading ? (
                    <p className="text-sm text-slate-600">Loading batch lines…</p>
                  ) : null}
                  {batchItemsQuery.isSuccess ? (
                    <div className="-mx-1 overflow-x-auto">
                      <table className="w-full min-w-[480px] text-left text-sm text-slate-800">
                        <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="w-10 px-1 py-2"> </th>
                            <th className="px-2 py-2">SKU</th>
                            <th className="px-2 py-2 text-right tabular-nums">Quantity</th>
                            <th className="px-2 py-2">Sales order</th>
                            <th className="px-2 py-2">Work order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(batchItemsQuery.data?.rows ?? []).length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-2 py-4">
                                <div className="flex flex-col items-center gap-3 text-center">
                                  <p className="text-sm text-slate-500">No lines on this batch yet.</p>
                                  <ReadOnlyHoverLayer
                                    active={!hubWritesAllowed}
                                    hoverHint={MANUFACTURING_HUB_READ_ONLY}
                                    layout="inline"
                                  >
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="danger"
                                      disabled={!hubWritesAllowed || deleteBatchMutation.isPending}
                                      isLoading={deleteBatchMutation.isPending}
                                      onClick={() => {
                                        if (selectedBatchId == null) return;
                                        const confirmed = window.confirm(
                                          `Delete batch ${String(selectedBatchId)}? This cannot be undone.`,
                                        );
                                        if (!confirmed) return;
                                        deleteBatchMutation.mutate(selectedBatchId);
                                      }}
                                    >
                                      Delete batch
                                    </Button>
                                  </ReadOnlyHoverLayer>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            (batchItemsQuery.data?.rows ?? []).map((row) => {
                              const removePending = pendingRemoveBatchItemIds.has(row.batch_item_id);
                              return (
                                <tr key={row.batch_item_id} className="border-b border-slate-50 last:border-none">
                                  <td className="px-1 py-2 align-middle">
                                    <ReadOnlyHoverLayer
                                      active={!hubWritesAllowed}
                                      hoverHint={
                                        hubWritesAllowed
                                          ? "Remove from batch and return to lines to schedule"
                                          : MANUFACTURING_HUB_READ_ONLY
                                      }
                                      layout="inline"
                                    >
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        className="inline-flex h-8 min-w-[2.25rem] items-center justify-center px-1.5 font-mono text-sm tabular-nums"
                                        disabled={!hubWritesAllowed || removePending}
                                        aria-label="Remove from batch"
                                        onClick={() => beginRemoveBatchLine(row.batch_item_id)}
                                      >
                                        {removePending ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                        ) : (
                                          "<<"
                                        )}
                                      </Button>
                                    </ReadOnlyHoverLayer>
                                  </td>
                                  <td className="whitespace-normal px-2 py-2 align-middle text-sm">
                                    <span className="inline-flex items-start gap-1">
                                      <span className="break-all font-mono">{row.sku}</span>
                                      <SalesOrderLineRemarkGlyph remark={row.remark} />
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-right align-middle tabular-nums">{String(row.quantity)}</td>
                                  <td className="whitespace-nowrap px-2 py-2 align-middle text-sm">
                                    {row.sales_order != null ? row.sales_order : "—"}
                                  </td>
                                  <td className="px-2 py-2 align-middle text-sm text-slate-700">
                                    {row.build_to_order ? (
                                      <span className="whitespace-pre-wrap">
                                        {row.build_order != null || row.internal_build_order != null
                                          ? `${[row.build_order, row.internal_build_order].filter(Boolean).join(" · ")}${
                                              row.build_order_rebuild === true ? " (rebuild)" : ""
                                            }`
                                          : "Pending work order"}
                                      </span>
                                    ) : (
                                      "Stock"
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </>
              ) : null}
            </CardBody>
          </Card>
        </div>
      ) : null}

      {newBatchDialogOpen ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-batch-new-batch-dialog-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h2 id="edit-batch-new-batch-dialog-title" className="text-lg font-semibold text-slate-900">
              New batch
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Choose where this batch will be built. Batches are limited to the ship-to address shown at the top of
              this page.
            </p>
            <form
              className="mt-4 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!hubWritesAllowed || createBatchMutation.isPending) return;
                createBatchMutation.mutate();
              }}
            >
              <label className="flex flex-col gap-1 text-xs text-slate-600">
                <span className="font-medium text-slate-700">Batch location</span>
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
                  <select
                    className="h-10 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900"
                    disabled={!hubWritesAllowed || createBatchMutation.isPending}
                    value={newBatchLocationId}
                    onChange={(event) => {
                      const value = Number.parseInt(event.target.value, 10);
                      if (Number.isFinite(value)) {
                        setNewBatchLocationId(value);
                      }
                    }}
                  >
                    {batchLocationOptionEntries().map(({ key, id }) => (
                      <option key={key} value={id}>
                        {formatLocationLabel(id)}
                      </option>
                    ))}
                  </select>
                </ReadOnlyHoverLayer>
              </label>
              {createBatchMutation.isError ? (
                <p className="text-sm text-rose-700">{createBatchMutation.error.message}</p>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={createBatchMutation.isPending}
                  onClick={() => {
                    setNewBatchDialogOpen(false);
                    createBatchMutation.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={createBatchMutation.isPending}
                  disabled={!hubWritesAllowed}
                >
                  Create batch
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <CreateAlternateOrderModal
        open={alternateOrderModalOpen}
        onClose={() => {
          setAlternateOrderModalOpen(false);
          setAlternateOrderPrefill(undefined);
        }}
        source="edit_batch"
        prefill={alternateOrderPrefill}
      />
    </div>
  );
}
